import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  GraduationCap,
  FileText,
  Wallet,
  Stethoscope,
  Sparkles,
  ChevronDown,
  Compass,
  Banknote,
  Calendar,
  ArrowUpRight,
} from 'lucide-react';
import InlineConsultationCTA from '@/components/common/InlineConsultationCTA';

/**
 * UKStudentVisaPage — premium content engine for /study-abroad/uk/student-visa/
 * ----------------------------------------------------------------------------
 * Hyper-optimised 2026 policy content rendered with the sandboxed Study
 * Abroad design tokens (navy + gold + Playfair). Fully responsive, with
 * FAQ JSON-LD schema and SEO meta injection.
 */

const SEO_TITLE = 'UK Student Visa 2026: Requirements, Fees & Process for Indians';
const SEO_DESCRIPTION =
  'UK Student Visa 2026 for Indian students: requirements, documents, funds, fees, timelines, refusal reasons & expert tips.';

// ─── Content Data ───────────────────────────────────────────────────
const REQUIRED_DOCS = [
  'Valid passport (with ≥6 months validity)',
  'CAS (Confirmation of Acceptance for Studies) from a UK university',
  'Academic transcripts & certificates',
  'English language test score (IELTS / PTE / SELT)',
  'UKVI-approved Tuberculosis (TB) certificate',
  'Financial evidence (28-day bank statement)',
  'ATAS certificate (for select STEM/research courses)',
  'Parental consent letter (applicants aged 16–17)',
];

const SUPPORTING_DOCS = [
  'University offer letter (conditional / unconditional)',
  'Statement of Purpose (SOP)',
  'Education loan sanction letter',
  'Sponsorship letter (if funded by relative)',
  'Travel history / previous visas',
  'Postgraduate CV / academic resume',
];

const APPLICATION_STEPS = [
  { title: 'Accept Offer & Receive CAS', desc: 'Confirm your place at a Student-Route licensed UK university and receive your unique CAS reference number.' },
  { title: 'Collect Financial & Academic Documents', desc: 'Compile transcripts, English test results, financial proof, ATAS (if required), and TB certificate.' },
  { title: 'Complete the Online Application', desc: 'File your Student visa application on the official UK Visas & Immigration portal.' },
  { title: 'Pay Visa Fee + IHS Surcharge', desc: 'Pay the £558 application fee plus the Immigration Health Surcharge (IHS) for full NHS access.' },
  { title: 'Book Biometrics & Submit Documents', desc: 'Schedule your biometric enrolment at a VFS Global centre and submit supporting paperwork.' },
  { title: 'Track Decision', desc: 'Standard processing is 3 weeks from biometrics. Use the SMS/email tracker until a decision is issued.' },
  { title: 'Travel After Visa Start Date', desc: 'Only enter the UK on or after the start date shown on your 30-day vignette / decision letter.' },
];

const REFUSAL_MISTAKES = [
  'Funds held for fewer than 28 continuous days',
  'Bank statement older than 31 days at submission',
  'Name mismatches across documents',
  'Poor-quality scans or unreadable evidence',
];

const ANCHOR_LINKS = [
  { label: 'UK Scholarships for Indian Students 2026', path: '/study-abroad/uk/scholarships/' },
  { label: 'Cost of Studying in UK for Indian Students 2026', path: '/study-abroad/uk/cost-of-study/' },
  { label: 'UK Intakes 2026', path: '/study-abroad/uk/intakes/' },
  { label: "Master's in UK for Indian Students 2026", path: '/study-abroad/uk/masters/' },
  { label: 'Education Loan Assistance', path: '/study-abroad/education-loan/' },
  { label: 'Scholarship Assessment', path: '/study-abroad/scholarship-assessment/' },
  { label: 'Talk to Our UK Counsellors', path: '/study-abroad/book-counselling/?source=study-abroad-uk-student-visa-anchor' },
];

const FAQS = [
  {
    q: 'How much is the UK Student visa fee in 2026?',
    a: 'The UK Student visa fee is £558 when applying from outside the UK. A revised fee schedule is expected from 8 April 2026 — confirm with our counsellors at the time of application.',
  },
  {
    q: 'How long do I need to hold my funds for the UK Student visa?',
    a: 'You must hold the required maintenance funds in your account for 28 continuous days. The closing balance date on the statement must be within 31 days of your visa application.',
  },
  {
    q: 'What is the minimum bank balance required?',
    a: 'For London-based courses, £1,529/month × up to 9 months. For courses outside London, £1,171/month × up to 9 months. Add the first year of tuition on top.',
  },
  {
    q: 'Is the Graduate visa still 2 years?',
    a: 'Yes — the Graduate visa remains 2 years for applications submitted on or before 31 December 2026. From 1 January 2027 it changes to 18 months.',
  },
  {
    q: 'Do I need IELTS for a UK Student visa?',
    a: 'Most universities accept IELTS, PTE Academic, or other approved SELTs. Some institutions waive the test if you studied your last qualification in English — verify with your CAS-issuing university.',
  },
  {
    q: 'How long does UK Student visa processing take?',
    a: 'Standard processing is around 3 weeks from your biometric appointment. Priority and Super-Priority services can reduce this to 5 working days and 1 working day respectively, for an additional fee.',
  },
  {
    q: 'Can I work in the UK on a Student visa?',
    a: 'Yes — up to 20 hours per week during term-time and full-time during official vacations, subject to your sponsor and visa conditions.',
  },
  {
    q: 'What is the TB test requirement for Indian applicants?',
    a: 'Indian applicants must complete a UKVI-approved TB test before applying. The certificate is valid for 6 months from the date of issue.',
  },
];

// ─── Subcomponents ──────────────────────────────────────────────────
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-8 lg:mb-12">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const BulletDot = ({ tone = 'gold' }) => {
  const toneMap = {
    gold: 'bg-[#C5A028]',
    blue: 'bg-[#003087]',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
  };
  return (
    <span className={`mt-1.5 inline-flex shrink-0 w-2 h-2 rounded-full ${toneMap[tone] || toneMap.gold}`} />
  );
};

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-2xl transition-all ${
        open ? 'border-[#C5A028]/50 bg-white shadow-md shadow-[#003087]/5' : 'border-slate-200 bg-white'
      }`}
      data-testid={`uk-visa-faq-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
        aria-expanded={open}
        data-testid={`uk-visa-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">{q}</span>
        <ChevronDown
          className={`shrink-0 w-5 h-5 mt-0.5 text-[#C5A028] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────
const UKStudentVisaPage = () => {
  // SEO meta + FAQ JSON-LD injection
  useEffect(() => {
    document.title = SEO_TITLE;

    // meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', SEO_DESCRIPTION);

    // FAQ JSON-LD schema
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.setAttribute('data-uk-visa-faq-schema', '1');
    scriptEl.text = JSON.stringify(schema);
    document.head.appendChild(scriptEl);

    // BreadcrumbList schema for SEO (Home → Study Abroad → UK → Student Visa)
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'UK', item: 'https://zafco.in/study-abroad/uk/' },
        { '@type': 'ListItem', position: 4, name: 'Student Visa Guide' },
      ],
    };
    const breadcrumbEl = document.createElement('script');
    breadcrumbEl.type = 'application/ld+json';
    breadcrumbEl.setAttribute('data-uk-visa-breadcrumb-schema', '1');
    breadcrumbEl.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbEl);

    return () => {
      if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
      const existing = document.head.querySelector('script[data-uk-visa-faq-schema]');
      if (existing) existing.remove();
      const existingBc = document.head.querySelector('script[data-uk-visa-breadcrumb-schema]');
      if (existingBc) existingBc.remove();
    };
    // All referenced names (SEO_TITLE, SEO_DESCRIPTION, FAQS) are module-level
    // constants and therefore stable across the component lifecycle — this
    // effect must run exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white" data-testid="uk-student-visa-page">
      {/* ============================== HERO ============================== */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl"
        />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-5">
              <ShieldCheck className="w-3.5 h-3.5" />
              UK Visa Guide · Updated for 2026
            </span>
            <h1
              className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5"
              data-testid="uk-visa-h1"
            >
              UK Student Visa for <span className="text-[#FACC15]">Indian Students 2026</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-6">
              Planning a UK student visa for Indian students in 2026 starts with one simple truth: success comes from
              meticulous evidence, perfectly held funds, and a CAS from a UK-licensed sponsor. This is the
              counsellor-grade guide to fees, documents, financial proof, processing windows, and refusal mitigation.
            </p>

            {/* Critical alert — visa fee + Graduate route */}
            <div
              className="flex gap-3 items-start bg-[#C5A028]/15 border border-[#C5A028]/45 rounded-2xl p-4 sm:p-5"
              data-testid="uk-visa-hero-alert"
            >
              <AlertTriangle className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" />
              <div className="text-sm sm:text-[15px] text-white leading-relaxed">
                <strong className="text-[#FACC15] font-bold">Key 2026 rule:</strong>{' '}
                Visa fee is <strong>£558 from outside the UK</strong>. The <strong>Graduate visa remains 2 years</strong>{' '}
                for applications on or before <strong>31 December 2026</strong>, then changes to{' '}
                <strong>18 months from 1 January 2027</strong>.
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link
                to="/study-abroad/book-counselling/?source=study-abroad-uk-student-visa"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:shadow-xl hover:shadow-[#C5A028]/30 hover:-translate-y-0.5"
                data-testid="uk-visa-hero-cta"
              >
                Book Free UK Visa Counselling
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/free-profile-evaluation/?source=study-abroad-uk-student-visa-hero&country=uk"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all"
              >
                Evaluate My Profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== VALUE & ELIGIBILITY ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Foundation"
            title="Why This Visa Matters & Eligibility Criteria"
            subtitle="A Student visa is more than entry permission — it is the gateway to the 2-year Graduate Route and your family's risk-management plan for an international degree investment."
          />

          <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-[#003087]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#003087]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087]">Why this visa matters</h3>
              </div>
              <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-3">
                The UK Student visa unlocks the Graduate Route — a 2-year post-study work permit (for applications
                submitted before 1 January 2027) that converts your education spend into real career capital across
                London, Manchester, Birmingham, and Edinburgh.
              </p>
              <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                For Indian families, a properly approved visa is also financial risk-management: it ratifies the
                university, the CAS, the maintenance funds, and the parental sponsorship chain — a layered defence
                against refusal scenarios.
              </p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-[#C5A028]/15 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#C5A028]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087]">Eligibility Criteria</h3>
              </div>
              <ul className="space-y-2.5">
                {[
                  'CAS issued by a Student-Route licensed UK sponsor (university/college)',
                  'Applicant aged 16 or older (parental consent required for 16–17)',
                  'Apply up to 6 months before course start (outside UK) or 3 months (inside UK)',
                  'Genuine Student requirement (academic progression + intent)',
                  'Approved English language proficiency for the chosen course',
                  'Sufficient maintenance funds held for 28 continuous days',
                ].map((rule) => (
                  <li key={rule} className="flex items-start gap-2.5">
                    <BulletDot tone="gold" />
                    <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== DOCUMENT CHECKLIST ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Document Checklist"
            title="What You'll Submit — Verified & Verifiable"
            subtitle="Group A is mandatory. Group B strengthens marginal cases and is often decisive for first-time applicants and gap-year students."
          />

          <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
            {/* Required */}
            <div className="bg-gradient-to-br from-[#003087] via-[#002467] to-[#020617] rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute -top-16 -right-12 w-56 h-56 rounded-full bg-[#C5A028]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FACC15]/15 border border-[#FACC15]/40 text-[10px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                  Group A · Required
                </div>
                <h3 className="text-lg sm:text-xl font-bold font-syne mb-5">Mandatory documents</h3>
                <ul className="space-y-2.5">
                  {REQUIRED_DOCS.map((d) => (
                    <li key={d} className="flex items-start gap-2.5">
                      <BulletDot tone="gold" />
                      <span className="text-sm sm:text-[15px] text-slate-200 leading-relaxed">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Supporting */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#003087]/10 border border-[#003087]/25 text-[10px] font-bold tracking-[0.22em] text-[#003087] uppercase mb-4">
                Group B · Useful Support
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087] mb-5">Strengthening evidence</h3>
              <ul className="space-y-2.5">
                {SUPPORTING_DOCS.map((d) => (
                  <li key={d} className="flex items-start gap-2.5">
                    <BulletDot tone="blue" />
                    <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Image placeholder */}
          <div
            className="mt-8 aspect-[16/5] rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 text-xs"
            role="img"
            aria-label="UK Student Visa checklist visual placeholder"
          >
            Image placeholder · UK Student Visa checklist
          </div>
        </div>
      </section>

      {/* ============================== FINANCIAL ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Money"
            title="Financial Capabilities & Maintenance Funds"
            subtitle="The Home Office requires evidence that you can pay tuition AND living costs without working full-time. The single most decisive document in your application."
          />

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 sm:p-8 border-b border-slate-100">
              <div className="flex items-start gap-3">
                <Wallet className="w-5 h-5 text-[#C5A028] mt-1 shrink-0" />
                <p className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">
                  Funds must be held for <strong>28 continuous days</strong> in a regulated bank account. The closing
                  balance date must fall within <strong>31 days</strong> of your visa application submission. Account
                  ownership: applicant or parent (with consent letter).
                </p>
              </div>
            </div>

            {/* Living costs table */}
            <div className="overflow-x-auto" data-testid="uk-visa-living-costs-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100/60 text-[11px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-5 sm:px-8 py-4 text-left font-bold">Location</th>
                    <th className="px-5 sm:px-8 py-4 text-left font-bold">Monthly Living Funds</th>
                    <th className="px-5 sm:px-8 py-4 text-left font-bold">Max Period Covered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 sm:px-8 py-5">
                      <div className="font-bold text-slate-900">London</div>
                      <div className="text-xs text-slate-500 mt-0.5">Inner & outer London boroughs</div>
                    </td>
                    <td className="px-5 sm:px-8 py-5 font-bold text-[#003087]">£1,529 / month</td>
                    <td className="px-5 sm:px-8 py-5 text-slate-700">9 months</td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 sm:px-8 py-5">
                      <div className="font-bold text-slate-900">Outside London</div>
                      <div className="text-xs text-slate-500 mt-0.5">Manchester, Birmingham, Edinburgh, etc.</div>
                    </td>
                    <td className="px-5 sm:px-8 py-5 font-bold text-[#003087]">£1,171 / month</td>
                    <td className="px-5 sm:px-8 py-5 text-slate-700">9 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Pro-tip warning callout */}
          <div
            className="mt-8 bg-amber-50 border border-amber-300/70 rounded-3xl p-6 sm:p-8"
            data-testid="uk-visa-funds-callout"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <h4 className="text-lg font-bold font-syne text-amber-900">
                4 Funds-Rule Reminders That Save Applications
              </h4>
            </div>
            <ul className="space-y-2.5 ml-8">
              {[
                'Funds must be held for 28 continuous days — no dips below the required amount.',
                'Closing balance date must fall within 31 days of submission.',
                'Parent sponsorship needs a signed declaration + relationship proof + their bank evidence.',
                'Loan letters must specify amount, applicant name, and disbursement schedule.',
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2.5">
                  <BulletDot tone="amber" />
                  <span className="text-sm sm:text-[15px] text-amber-900/90 leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Image placeholder */}
          <div
            className="mt-8 aspect-[16/5] rounded-2xl border-2 border-dashed border-slate-200 bg-white flex items-center justify-center text-slate-400 text-xs"
            role="img"
            aria-label="London vs Outside London comparison chart placeholder"
          >
            Image placeholder · London vs Outside London comparison chart
          </div>
        </div>
      </section>

      {/* ============================== STEPS TIMELINE ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Timeline"
            title="Step-by-Step Visa Application Process"
            subtitle="Follow this sequence in order. Skipping or reordering steps (e.g., booking biometrics before paying fees) commonly triggers system errors and delays."
          />

          <div className="relative max-w-3xl mx-auto" data-testid="uk-visa-steps-timeline">
            {APPLICATION_STEPS.map((step, idx) => (
              <div key={step.title} className="relative pl-16 sm:pl-20 pb-8 last:pb-0">
                {/* Vertical line */}
                {idx < APPLICATION_STEPS.length - 1 && (
                  <span className="absolute left-[19px] sm:left-[23px] top-12 bottom-0 w-px bg-gradient-to-b from-[#C5A028] via-[#C5A028]/40 to-transparent" />
                )}
                {/* Numbered badge */}
                <span className="absolute left-0 top-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#C5A028] to-[#EAB308] text-black font-bold flex items-center justify-center text-base sm:text-lg shadow-lg shadow-[#C5A028]/30 ring-4 ring-white">
                  {idx + 1}
                </span>
                <div className="bg-slate-50 hover:bg-slate-100/70 transition-colors border border-slate-200 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold font-syne text-[#003087] mb-1">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== FEES, HEALTH, REFUSAL ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Fine Print"
            title="Fees, Health Rules & Mitigating Common Refusals"
            subtitle="The three operational areas where most Indian applications stumble. Mastering all three lifts your approval probability dramatically."
          />

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-7">
            {/* Fees & Timing */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-7">
              <div className="w-11 h-11 rounded-2xl bg-[#003087]/10 flex items-center justify-center mb-4">
                <Banknote className="w-5 h-5 text-[#003087]" />
              </div>
              <h3 className="text-base sm:text-lg font-bold font-syne text-[#003087] mb-3">Fees & Timing</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                The application fee is <strong className="text-slate-900">£558</strong> from outside the UK, plus the
                Immigration Health Surcharge (IHS) for full NHS access during your stay.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                A fee revision is scheduled for <strong className="text-slate-900">8 April 2026</strong>. Plan submission
                windows around this update — applications lodged before that date are charged at the current rate.
              </p>
            </div>

            {/* TB Test & Health */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-7">
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                <Stethoscope className="w-5 h-5 text-emerald-700" />
              </div>
              <h3 className="text-base sm:text-lg font-bold font-syne text-[#003087] mb-3">TB Test & Health Rules</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Indian applicants must complete a <strong className="text-slate-900">UKVI-approved TB test</strong>{' '}
                before submitting their application.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                The certificate is valid for <strong className="text-slate-900">6 months</strong> from the date of
                issue — schedule it within your application window to avoid expiry.
              </p>
            </div>

            {/* Refusal Mitigation */}
            <div className="bg-white rounded-3xl border border-red-200 p-6 sm:p-7">
              <div className="w-11 h-11 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-base sm:text-lg font-bold font-syne text-red-700 mb-3">Avoid These Mistakes</h3>
              <ul className="space-y-2">
                {REFUSAL_MISTAKES.map((m) => (
                  <li key={m} className="flex items-start gap-2.5">
                    <BulletDot tone="red" />
                    <span className="text-sm text-slate-700 leading-relaxed">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== STRATEGY ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Strategy"
            title="Strategic Cost Planning & Improving Success Rates"
            subtitle="Treat your visa application as a single project with parallel work-streams: financial readiness, scholarship pipeline, course selection, and document hygiene."
          />

          <div className="max-w-4xl space-y-5 text-sm sm:text-[15px] text-slate-700 leading-relaxed">
            <p>
              <strong className="text-slate-900">Financial readiness checks</strong> begin 6 months before
              submission. Calculate tuition + maintenance + IHS + ATAS (if applicable) + flight + accommodation
              deposit, then ensure the maintenance portion has lived in your nominated account for at least 28 days.
            </p>
            <p>
              <strong className="text-slate-900">Early scholarship planning</strong> can offset £5,000–£20,000 of
              tuition. Russell Group universities open scholarship applications 6–9 months ahead of the September
              intake — apply before you receive your unconditional CAS.
            </p>
            <p>
              <strong className="text-slate-900">Shorter degree advantage:</strong> 12-month UK Master&apos;s programmes
              deliver the Graduate Route benefit with one year of tuition — materially better ROI than 2-year
              equivalents in other geographies.
            </p>
            <p>
              <strong className="text-slate-900">Practical filing tips:</strong> use the official UK universities&apos;
              CAS guidance verbatim, scan documents in greyscale at 300 DPI, label files with{' '}
              <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[12.5px]">
                Surname_DocType.pdf
              </code>{' '}
              naming, and front-load financial evidence as the very first attachment.
            </p>
          </div>

          {/* Anchor chips */}
          <div className="mt-8">
            <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-slate-500 mb-3">
              Related Reading & Tools
            </div>
            <div className="flex flex-wrap gap-2.5">
              {ANCHOR_LINKS.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="inline-flex items-center gap-1.5 bg-slate-50 hover:bg-[#003087] hover:text-white border border-slate-200 text-slate-700 text-xs sm:text-[13px] font-semibold px-4 py-2 rounded-full transition-colors"
                  data-testid={`uk-visa-anchor-${l.path.split('/').filter(Boolean).slice(-2, -1)[0] || 'tool'}`}
                >
                  <Compass className="w-3 h-3" />
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================== FAQ ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Answers"
            title="Frequently Asked Questions"
            subtitle="The 8 questions Indian families ask most often, refined by our counselling team for the 2026 application cycle."
          />

          <div className="space-y-3" data-testid="uk-visa-faq-list">
            {FAQS.map((faq, idx) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================== INLINE LEAD CAPTURE FORM (Zoho CRM) ============================== */}
      <InlineConsultationCTA
        eyebrow="Free UK Student Visa Counselling"
        headline="Get Personalised UK Student Visa Guidance — Free 1-on-1 Session"
        subheadline="Share your profile and a senior UK counsellor will reach out within 60 seconds on WhatsApp. Refusal-proof your application from day 1."
        bullets={[
          'CAS, ATAS, financial documents & TB test — covered in one session',
          'MEA-approved · 10,000+ students placed since 2015',
          'Free profile + scholarship review before any commitment',
        ]}
        testId="uk-visa-inline-consultation"
      />

      {/* ============================== LEAD CAPTURE ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div
            className="relative overflow-hidden rounded-3xl text-white"
            style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
            data-testid="uk-visa-lead-capture"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C5A028]/16 blur-3xl"
            />
            <div className="relative p-8 sm:p-12 lg:p-16 grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/18 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                  <GraduationCap className="w-3.5 h-3.5" />
                  ZAFCO UK Counselling Desk
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-bold font-syne leading-tight mb-4">
                  Planning to study in the UK in 2026 or 2027?
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6 max-w-xl">
                  Book a free counselling session with Zafco for your UK student visa for Indian students 2026 profile
                  review, scholarship assessment, and education loan guidance.
                </p>
                <Link
                  to="/study-abroad/book-counselling/?source=study-abroad-uk-student-visa"
                  className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-4 rounded-full transition-all hover:shadow-2xl hover:shadow-[#C5A028]/40 hover:-translate-y-0.5"
                  data-testid="uk-visa-lead-capture-cta"
                >
                  Book Free Visa Counselling Session
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:block">
                <ul className="space-y-3">
                  {[
                    { Icon: FileText, label: 'CAS & document audit by certified counsellors' },
                    { Icon: Wallet, label: 'Maintenance funds + parent sponsorship roadmap' },
                    { Icon: Calendar, label: 'September & January 2026/27 intake planning' },
                    { Icon: ShieldCheck, label: 'Refusal-risk pre-check before submission' },
                  ].map(({ Icon, label }) => (
                    <li
                      key={label}
                      className="flex items-start gap-3 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3"
                    >
                      <Icon className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-200">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UKStudentVisaPage;
